import { darkTheme } from '../../theme/tokens';
import { Spinner } from '../ui/Spinner';

const t = darkTheme;

export interface ToolPillProps {
  /** User-facing label (e.g. "Field values"). What renders in the row. */
  displayName: string;
  result?: { summary: string; isError: boolean };
  // Reserved for SC-1612 Part B confirmation UI. Rendered below the row when present.
  actionFooter?: React.ReactNode;
}

export function ToolPill({ displayName, result, actionFooter }: ToolPillProps) {
  const pending = !result;
  const isError = result?.isError === true;

  const iconColor = isError ? t.amber : t.text3;
  const labelColor = isError ? t.amberText : t.text2;
  const summaryColor = isError ? t.amberText : t.text3;

  const status = pending ? 'Running…' : result.summary;

  // No aria-label on the row. The parent ChatAssistantBubble wraps this in an
  // aria-live="polite" region whose visible text (displayName + status) is
  // what SRs announce. aria-label on a roleless <div> is ignored; adding
  // role="status" instead would nest live regions and risk double-announce.

  return (
    <div style={{ margin: '6px 0' }}>
      <div
        data-testid="tool-pill"
        data-state={pending ? 'pending' : isError ? 'error' : 'done'}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12,
          lineHeight: 1.4,
          color: summaryColor,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            flexShrink: 0,
            width: 12,
            display: 'inline-flex',
            justifyContent: 'center',
            color: iconColor,
            fontSize: 11,
          }}
        >
          {pending ? <Spinner size={11} /> : isError ? '⚠' : '✓'}
        </span>
        <span
          style={{
            fontWeight: 600,
            color: labelColor,
            flexShrink: 0,
          }}
        >
          {displayName}
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {status}
        </span>
      </div>
      {actionFooter && <div style={{ marginTop: 6 }}>{actionFooter}</div>}
    </div>
  );
}
